import { createMutationHook, createQueryHook } from '@/common/hooks/typedHookFactory';
import { QueryKeys } from '@/common/queryKeys';
import { graphql } from '@/generated/graphql';
import type { GetUsersQuery } from '@/generated/graphql/graphql';

export type UserDepartment = GetUsersQuery['user'][number]['departments_users'][number];
export type ExtendedUser = GetUsersQuery['user'][number];

const GET_USERS = graphql(`
  query GetUsers(
    $limit: Int = 10
    $offset: Int = 0
    $where: user_bool_exp = {}
    $sorters: [user_order_by!]!
  ) {
    user(limit: $limit, offset: $offset, order_by: $sorters, where: $where) {
      blocked
      createdAt
      email
      id
      name
      roles
      firebaseMessagingToken
      telegramName
      login_logs(limit: 1, order_by: { lastSeenAt: desc }) {
        device
        appVersion
      }
      departments_users {
        id
        departmentId
        department {
          id
          name
        }
      }
    }
    user_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

const GET_USER_META = graphql(`
  query GetUserMeta {
    user(distinct_on: role) {
      role
    }
    department {
      id
      name
    }
  }
`);

const CREATE_USER = graphql(`
  mutation CreateUser(
    $email: String!
    $name: String!
    $roles: [String!]!
    $departmentIds: [Int!]!
    $departmentId: Int!
    $metadata: jsonb!
    $telegramName: String!
  ) {
    createUser(
      user: {
        email: $email
        name: $name
        roles: $roles
        metadata: $metadata
        departmentIds: $departmentIds
        departmentId: $departmentId
        telegramName: $telegramName
      }
    ) {
      userKey
    }
  }
`);

const BLOCK_USER = graphql(`
  mutation BlockUser($userId: String!) {
    blockUser(userId: $userId) {
      applied
    }
  }
`);

const EDIT_USER = graphql(`
  mutation EditUser($id: String!, $user: UserEditInput!) {
    editUser(userId: $id, user: $user) {
      applied
    }
  }
`);

const UPDATE_USER_TELEGRAM = graphql(`
  mutation UpdateUserTelegram($id: String!, $telegramName: String!) {
    update_user_by_pk(pk_columns: { id: $id }, _set: { telegramName: $telegramName }) {
      id
      telegramName
    }
  }
`);

export const useGetUsers = createQueryHook(GET_USERS, (variables) => [
  QueryKeys.GET_USERS,
  JSON.stringify(variables),
]);

export const useGetUserMeta = createQueryHook(GET_USER_META, () => [QueryKeys.GET_USER_META]);

export const useCreateUser = createMutationHook(CREATE_USER, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_USERS],
    },
  ],
  errorMessage: 'Error creating user',
});

export const useBlockUser = createMutationHook(BLOCK_USER, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_USERS],
    },
  ],
  errorMessage: 'Error blocking user',
});

export const useEditUser = createMutationHook(EDIT_USER, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_USERS],
    },
    {
      queryKey: () => [QueryKeys.GET_META],
    },
    {
      queryKey: () => [QueryKeys.GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES],
    },
    {
      queryKey: () => [QueryKeys.GET_ORDER_LIST_BY_ID_WITH_DRIVERS_WAREHOUSES],
    },
    {
      queryKey: () => [QueryKeys.GET_DRIVERS_LIST],
    },
    {
      queryKey: () => [QueryKeys.GET_DRIVERS_WITH_LAST_TRACK_POINT],
    },
    {
      queryKey: () => [QueryKeys.GET_DRIVERS_WITH_ADDRESS_BY_DATE],
    },
  ],
  errorMessage: 'Error updating user',
});

export const useUpdateUserTelegram = createMutationHook(UPDATE_USER_TELEGRAM, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_USERS],
    },
  ],
  errorMessage: 'Error updating user telegram',
});
